import {
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Cancel as CancelIcon,
  Close as CloseIcon,
  History as HistoryIcon,
  Replay as ReplayIcon,
  UploadFile as UploadFileIcon,
} from '@mui/icons-material';
import type { UploadEntry, UploadStatus } from '../hooks/useFileUploads';

type UploadProgressPanelProps = {
  entries: UploadEntry[];
  open: boolean;
  onClose: () => void;
  onCancel: (id: string) => void;
  onRetry: (id: string) => void;
};

const statusLabel: Record<UploadStatus, string> = {
  queued: 'Queued',
  uploading: 'Uploading',
  done: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const statusColor = (status: UploadStatus): 'default' | 'success' | 'error' | 'warning' => {
  if (status === 'done') return 'success';
  if (status === 'failed') return 'error';
  if (status === 'cancelled') return 'warning';
  return 'default';
};

const formatBytes = (bytes: number): string => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

const UploadRow = ({
  entry,
  onCancel,
  onRetry,
}: {
  entry: UploadEntry;
  onCancel: (id: string) => void;
  onRetry: (id: string) => void;
}) => {
  const isActive = entry.status === 'queued' || entry.status === 'uploading';
  const remaining = Math.max(entry.file.size - entry.uploadedBytes, 0);

  return (
    <Box sx={{ py: 1.5 }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <UploadFileIcon fontSize="small" color="action" sx={{ mt: 0.25 }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" noWrap title={entry.file.name} sx={{ mb: 0.5 }}>
            {entry.file.name}
          </Typography>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Typography variant="caption" color="text.secondary">
              {entry.percent}% · {formatBytes(entry.uploadedBytes)} uploaded
            </Typography>
            <Chip
              size="small"
              label={statusLabel[entry.status]}
              color={statusColor(entry.status)}
            />
          </Stack>
          {isActive && (
            <>
              <LinearProgress
                variant={entry.status === 'queued' ? 'indeterminate' : 'determinate'}
                value={entry.percent}
                sx={{ mt: 0.75 }}
              />
              <Typography variant="caption" color="text.secondary">
                {entry.status === 'queued'
                  ? 'Waiting to start'
                  : entry.isProcessing
                    ? 'Processing on server...'
                    : `${formatBytes(remaining)} remaining`}
              </Typography>
            </>
          )}
          {entry.error && (
            <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.5 }}>
              {entry.error}
            </Typography>
          )}
        </Box>
        {isActive ? (
          <Tooltip title="Cancel upload">
            <IconButton size="small" onClick={() => onCancel(entry.id)} aria-label="Cancel upload">
              <CancelIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : entry.status === 'failed' ? (
          <Tooltip title="Retry upload">
            <IconButton size="small" onClick={() => onRetry(entry.id)} aria-label="Retry upload">
              <ReplayIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
      </Stack>
    </Box>
  );
};

export const UploadProgressPanel = ({
  entries,
  open,
  onClose,
  onCancel,
  onRetry,
}: UploadProgressPanelProps) => {
  const active = entries.filter(
    (entry) => entry.status === 'queued' || entry.status === 'uploading'
  );
  const history = entries.filter(
    (entry) => entry.status === 'done' || entry.status === 'failed' || entry.status === 'cancelled'
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        sx: {
          width: { xs: 'min(100vw, 360px)', sm: 380 },
          p: 2,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center" spacing={1}>
          <HistoryIcon color="primary" />
          <Typography variant="h6">Operations</Typography>
        </Stack>
        <IconButton onClick={onClose} aria-label="Close operations panel">
          <CloseIcon />
        </IconButton>
      </Stack>

      <Box sx={{ flex: 1, overflow: 'auto', mt: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          Active uploads ({active.length})
        </Typography>
        {active.length === 0 ? (
          <Typography variant="body2" color="text.disabled" sx={{ py: 1.5 }}>
            No active uploads
          </Typography>
        ) : (
          active.map((entry) => (
            <UploadRow key={entry.id} entry={entry} onCancel={onCancel} onRetry={onRetry} />
          ))
        )}

        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle2" color="text.secondary">
          History ({history.length})
        </Typography>
        {history.length === 0 ? (
          <Typography variant="body2" color="text.disabled" sx={{ py: 1.5 }}>
            Completed operations appear here
          </Typography>
        ) : (
          [...history]
            .reverse()
            .map((entry) => (
              <UploadRow key={entry.id} entry={entry} onCancel={onCancel} onRetry={onRetry} />
            ))
        )}
      </Box>
    </Drawer>
  );
};
